import { useState, useEffect } from 'react';
import axios from 'axios';
import { sendNotification, sendNotificationWithAction } from 'utils/helperMethods';
import { tradePromoOptimizationUrls } from 'config/urls';

export enum OptimizationStatus {
    PLAN_OPTIMIZED = 'success',
    PLAN_OPTIMIZATION_IN_PROGRESS = 'in_progress',
    PLAN_OPTIMIZATION_FAILED = 'failed',
}

export interface OptimizationStatusResponse {
    plan_id: number;
    opt_plan_id: number;
    opt_plan_name: string;
    opt_plan_error: string;
}

const OPTIMIZATION_STATUS_POLLING_INTERVAL = 30000; // 30 seconds

const useOptimizationStatus = (
    planId: number | null,
    enabled: boolean = true,
    planName: string = '',
    onComplete: any,
    setIsOptimizing: any,
    isOptimizing: boolean,
) => {
    const [optimizationStatus, setOptimizationStatus] = useState<OptimizationStatus | null>(null);
    const [optimizationData, setOptimizationData] = useState<OptimizationStatusResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchStatus = async () => {
        if (!enabled || !planId) return;

        setIsLoading(true);
        try {
            const { data } = await axios.get(`${tradePromoOptimizationUrls.getOptimizedPlanStatus}/${planId}`);
            setOptimizationData(data);

            let currentStatus: OptimizationStatus | null = null;

            if (data.opt_plan_status === OptimizationStatus.PLAN_OPTIMIZED) {
                currentStatus = OptimizationStatus.PLAN_OPTIMIZED;
            }
            if (data.opt_plan_status === OptimizationStatus.PLAN_OPTIMIZATION_IN_PROGRESS) {
                currentStatus = OptimizationStatus.PLAN_OPTIMIZATION_IN_PROGRESS;
            } else if (data.opt_plan_error) {
                currentStatus = OptimizationStatus.PLAN_OPTIMIZATION_FAILED;
            }
            if (currentStatus) {
                setOptimizationStatus(currentStatus);

                switch (currentStatus) {
                    case OptimizationStatus.PLAN_OPTIMIZED:
                        onComplete(data.opt_plan_id);
                        sendNotificationWithAction(
                            'success',
                            `${planName} Optimised successful!`,
                            10,
                            350,
                            'Click here',
                            () => onComplete(data.opt_plan_id),
                        );
                        setIsOptimizing(false);
                        break;
                    case OptimizationStatus.PLAN_OPTIMIZATION_IN_PROGRESS:
                        sendNotification('info', `${planName} taking longer than usual ....`, 30, 350);
                        setIsOptimizing(true);
                        break;
                    case OptimizationStatus.PLAN_OPTIMIZATION_FAILED:
                        sendNotification('error', `${planName} Failed to Optimise.`);
                        setIsOptimizing(false);
                        break;
                }
            }
            setError(null);
        } catch (err: any) {
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isOptimizing) return;
            fetchStatus();
        }, OPTIMIZATION_STATUS_POLLING_INTERVAL);

        return () => clearInterval(interval);
    }, [planId, enabled, isOptimizing]);

    useEffect(() => {
        setOptimizationStatus(null);
        setOptimizationData(null);
    }, [planId]);

    return {
        optimizationStatus,
        optimizationData,
        isLoading,
        error,
    };
};

export default useOptimizationStatus;
